import { useEffect, useMemo, useState, useCallback } from 'react';
import HeaderMaster from '../components/HeaderMaster';
import SideBarMaster from '../components/SideBarMaster';
import ClienteDetalheMaster from '../components/ClienteDetalheMaster';
import styles from './ClientesMaster.module.css';
import { MdSearch, MdRefresh } from 'react-icons/md';
import { fetchWithAuth } from '../../../shared/utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const ROTULO_NIVEL = { saudavel: 'Saudável', atencao: 'Atenção', critico: 'Crítico', inativo: 'Inativo' };
const ORDEM_NIVEL = { critico: 0, atencao: 1, saudavel: 2, inativo: 3 };
const INTEGRACOES = [['whatsapp', 'WhatsApp'], ['ifood', 'iFood'], ['mercadopago', 'Mercado Pago'], ['fiscal', 'Fiscal']];

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

function textoUltimoPedido(c) {
  const dias = c.metricas.dias_sem_pedido;
  if (dias === null) return 'nunca';
  if (dias === 0) return 'hoje';
  return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

function ClientesMaster() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtroSaude, setFiltroSaude] = useState('todos');
  const [filtroComercial, setFiltroComercial] = useState('todos');
  const [aberto, setAberto] = useState(null); // id do cliente com o painel de detalhes aberto

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/master/clientes/saude`);
      const corpo = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) throw new Error(corpo.error || 'Falha ao carregar os clientes');
      setDados(corpo);
      setErro(null);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const clientes = useMemo(() => {
    if (!dados) return [];
    const termo = busca.trim().toLowerCase();
    return dados.clientes
      .filter((c) => (
        (filtroSaude === 'todos' || c.saude.nivel === filtroSaude) &&
        (filtroComercial === 'todos' || c.situacao_comercial.codigo === filtroComercial) &&
        (!termo || c.nome.toLowerCase().includes(termo) || (c.nome_admin || '').toLowerCase().includes(termo))
      ))
      .sort((a, b) => ORDEM_NIVEL[a.saude.nivel] - ORDEM_NIVEL[b.saude.nivel] || a.nome.localeCompare(b.nome));
  }, [dados, busca, filtroSaude, filtroComercial]);

  const resumo = dados?.resumo;
  const clienteAberto = dados?.clientes.find((c) => c.id === aberto) || null;

  return (
    <div className={styles.secaoPrincipal}>
      <SideBarMaster />
      <div className={styles.containerDashboard}>
        <HeaderMaster />
        <div className={styles.contentDashboard}>
          <div className={styles.tituloSecao}>
            <div>
              <h2>Clientes</h2>
              <p className={styles.subtitulo}>Quem está usando o sistema de verdade, quem precisa de atenção e como está a cobrança.</p>
            </div>
            <button className={styles.btnAtualizar} onClick={carregar} disabled={carregando}>
              <MdRefresh /> Atualizar
            </button>
          </div>

          {erro && <p className={styles.erro}>{erro}</p>}

          {resumo && (
            <>
              <div className={styles.statsCards}>
                <button className={`${styles.statCard} ${filtroSaude === 'critico' ? styles.statAtivo : ''}`} onClick={() => setFiltroSaude(filtroSaude === 'critico' ? 'todos' : 'critico')}>
                  <h3>Críticos</h3><p className={styles.statNumber} style={{ color: 'var(--sh-off)' }}>{resumo.critico}</p>
                </button>
                <button className={`${styles.statCard} ${filtroSaude === 'atencao' ? styles.statAtivo : ''}`} onClick={() => setFiltroSaude(filtroSaude === 'atencao' ? 'todos' : 'atencao')}>
                  <h3>Atenção</h3><p className={styles.statNumber} style={{ color: 'var(--sh-pending)' }}>{resumo.atencao}</p>
                </button>
                <button className={`${styles.statCard} ${filtroSaude === 'saudavel' ? styles.statAtivo : ''}`} onClick={() => setFiltroSaude(filtroSaude === 'saudavel' ? 'todos' : 'saudavel')}>
                  <h3>Saudáveis</h3><p className={styles.statNumber} style={{ color: 'var(--sh-live)' }}>{resumo.saudavel}</p>
                </button>
                <button className={`${styles.statCard} ${filtroSaude === 'inativo' ? styles.statAtivo : ''}`} onClick={() => setFiltroSaude(filtroSaude === 'inativo' ? 'todos' : 'inativo')}>
                  <h3>Inativos</h3><p className={styles.statNumber}>{resumo.inativo}</p>
                </button>
              </div>

              <div className={styles.statsCards}>
                <button className={`${styles.statCard} ${filtroComercial === 'atrasada' ? styles.statAtivo : ''}`} onClick={() => setFiltroComercial(filtroComercial === 'atrasada' ? 'todos' : 'atrasada')}>
                  <h3>Cobrança atrasada</h3>
                  <p className={styles.statNumber} style={{ color: resumo.comercial.atrasada ? 'var(--sh-off)' : undefined }}>{resumo.comercial.atrasada}</p>
                  {resumo.comercial.valor_em_atraso > 0 && <span className={styles.statNota}>{dinheiro(resumo.comercial.valor_em_atraso)} em aberto</span>}
                </button>
                <button className={`${styles.statCard} ${filtroComercial === 'em_teste' ? styles.statAtivo : ''}`} onClick={() => setFiltroComercial(filtroComercial === 'em_teste' ? 'todos' : 'em_teste')}>
                  <h3>Em teste</h3><p className={styles.statNumber}>{resumo.comercial.em_teste}</p>
                  {resumo.comercial.teste_expirado > 0 && <span className={styles.statNota}>{resumo.comercial.teste_expirado} expirado(s)</span>}
                </button>
                <button className={`${styles.statCard} ${filtroComercial === 'em_dia' ? styles.statAtivo : ''}`} onClick={() => setFiltroComercial(filtroComercial === 'em_dia' ? 'todos' : 'em_dia')}>
                  <h3>Em dia</h3><p className={styles.statNumber} style={{ color: 'var(--sh-live)' }}>{resumo.comercial.em_dia}</p>
                </button>
                <button className={`${styles.statCard} ${filtroComercial === 'sem_plano' ? styles.statAtivo : ''}`} onClick={() => setFiltroComercial(filtroComercial === 'sem_plano' ? 'todos' : 'sem_plano')}>
                  <h3>Sem plano</h3><p className={styles.statNumber} style={{ color: resumo.comercial.sem_plano ? 'var(--sh-pending)' : undefined }}>{resumo.comercial.sem_plano}</p>
                </button>
                <div className={styles.statCard}>
                  <h3>Previsto no mês</h3><p className={styles.statNumber}>{dinheiro(resumo.comercial.previsto_mes)}</p>
                  <span className={styles.statNota}>mensalidades + comissão até agora</span>
                </div>
              </div>

              <section className={styles.funil}>
                <h3 className={styles.funilTitulo}>Funil de ativação <span>clientes ativos que chegaram em cada passo</span></h3>
                {dados.funil.map((passo) => (
                  <div key={passo.id} className={styles.funilLinha}>
                    <span className={styles.funilRotulo}>{passo.rotulo}</span>
                    <div className={styles.funilBarra}><div className={styles.funilPreenchido} style={{ width: `${passo.percentual}%` }} /></div>
                    <span className={styles.funilNumero}>{passo.quantidade} <small>({passo.percentual}%)</small></span>
                  </div>
                ))}
              </section>
            </>
          )}

          <div className={styles.filtros}>
            <div className={styles.searchBar}>
              <MdSearch className={styles.searchIcon} />
              <input className={styles.searchInput} placeholder="Pesquisar por restaurante ou administrador..." value={busca} onChange={(e) => setBusca(e.target.value)} />
            </div>
            {(filtroSaude !== 'todos' || filtroComercial !== 'todos') && (
              <button className={styles.btnLimpar} onClick={() => { setFiltroSaude('todos'); setFiltroComercial('todos'); }}>Limpar filtros</button>
            )}
          </div>

          <div className={styles.tableContainer}>
            {carregando && !dados ? (
              <p className={styles.vazio}>Carregando...</p>
            ) : clientes.length === 0 ? (
              <p className={styles.vazio}>Nenhum cliente com esses filtros.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Cliente</th><th>Saúde</th><th>Último pedido</th><th>Pedidos (30d)</th><th>Faturamento (30d)</th>
                    <th>Ativação</th><th>Integrações</th><th>Plano</th><th />
                  </tr>
                </thead>
                <tbody>
                  {clientes.map((c) => (
                    <tr key={c.id} onClick={() => setAberto(c.id)} className={styles.linha}>
                      <td>
                        <div className={styles.nome}>{c.nome}</div>
                        <div className={styles.meta}>{[c.segmento, c.nome_admin].filter(Boolean).join(' · ') || '—'}</div>
                      </td>
                      <td>
                        <span className={`${styles.pill} ${styles[`nivel_${c.saude.nivel}`]}`}>{ROTULO_NIVEL[c.saude.nivel]}</span>
                        {c.saude.motivos[0] && <div className={styles.motivo}>{c.saude.motivos[0]}{c.saude.motivos.length > 1 ? ` +${c.saude.motivos.length - 1}` : ''}</div>}
                      </td>
                      <td className={styles.mono}>{textoUltimoPedido(c)}</td>
                      <td className={styles.mono}>
                        {c.metricas.pedidos_30d}
                        {c.metricas.variacao_30d !== null && (
                          <span className={c.metricas.variacao_30d < 0 ? styles.queda : styles.alta}> {c.metricas.variacao_30d > 0 ? '+' : ''}{c.metricas.variacao_30d}%</span>
                        )}
                      </td>
                      <td className={styles.mono}>{dinheiro(c.metricas.faturamento_30d)}</td>
                      <td>
                        <div className={styles.barraAtivacao} title={c.funil.proximo_passo || 'Ativação completa'}>
                          <div className={styles.barraAtivacaoPreenchida} style={{ width: `${c.funil.percentual}%` }} />
                        </div>
                        <span className={styles.meta}>{c.funil.concluidos}/{c.funil.total}</span>
                      </td>
                      <td>
                        <div className={styles.chips}>
                          {INTEGRACOES.filter(([chave]) => c.integracoes[chave]).map(([chave, rotulo]) => (
                            <span key={chave} className={styles.chipLigado}>{rotulo}</span>
                          ))}
                          {!INTEGRACOES.some(([chave]) => c.integracoes[chave]) && <span className={styles.chipDesligado}>nenhuma</span>}
                        </div>
                      </td>
                      <td><span className={`${styles.pill} ${styles[`com_${c.situacao_comercial.codigo}`]}`}>{c.situacao_comercial.rotulo}</span></td>
                      <td><span className={styles.abrir}>Abrir →</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {clienteAberto && (
        <ClienteDetalheMaster cliente={clienteAberto} onFechar={() => setAberto(null)} onMudou={carregar} />
      )}
    </div>
  );
}

export default ClientesMaster;
